import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { prefersStill, useLoadingStyle } from './useLoadingStyle'
import { capDpr, cappedLinks, sectionStarfieldPoints, shouldDrawFrame, stepDots } from './loaderDraw'
import { COLOR_PICKER_SWATCHES } from './colorPickerConstants'
import './loaders.css'

export interface SectionLoaderProps {
  count?: number
  className?: string
  style?: CSSProperties
}

export const SectionLoader = ({
  count = 16,
  className,
  style,
}: SectionLoaderProps) => {
  const loadingStyle = useLoadingStyle()
  const still = prefersStill()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const starfieldPoints = useMemo(() => {
    return loadingStyle === 'starfield' ? sectionStarfieldPoints() : []
  }, [loadingStyle])

  useEffect(() => {
    if (loadingStyle !== 'constellation') return
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = capDpr(typeof window !== 'undefined' ? window.devicePixelRatio : 1)
    const FRAME_MS = 1000 / 30

    let rafId = 0
    let lastTime = 0
    let inView = true
    let isHidden = typeof document !== 'undefined' ? document.hidden : false
    let visible = inView && !isHidden

    const cs = getComputedStyle(canvas)
    const lineColor = cs.getPropertyValue('--loader-line').trim() || cs.getPropertyValue('--border').trim() || 'currentColor'
    const dotColor = cs.getPropertyValue('--loader-accent').trim() || COLOR_PICKER_SWATCHES[0]

    let w = 0
    let h = 0
    let dots: Array<{ x: number; y: number; vx: number; vy: number }> = []

    const drawFrame = () => {
      ctx.clearRect(0, 0, w, h)

      ctx.lineWidth = 0.8
      ctx.strokeStyle = lineColor
      const links = cappedLinks(dots, 140, 3)
      for (let i = 0; i < links.length; i++) {
        const [aIdx, bIdx, alpha] = links[i]
        ctx.globalAlpha = alpha
        ctx.beginPath()
        ctx.moveTo(dots[aIdx].x, dots[aIdx].y)
        ctx.lineTo(dots[bIdx].x, dots[bIdx].y)
        ctx.stroke()
      }

      ctx.globalAlpha = 1
      ctx.fillStyle = dotColor
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i]
        ctx.beginPath()
        ctx.arc(d.x, d.y, 1.9, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    const fit = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width || 200
      h = rect.height || 118

      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      dots = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
      }))

      if (still) {
        drawFrame()
      }
    }

    fit()

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        fit()
      })
      ro.observe(canvas)
    }

    if (still) {
      return () => {
        cancelAnimationFrame(rafId)
        ro?.disconnect()
      }
    }

    let io: IntersectionObserver | null = null
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([entry]) => {
        inView = entry ? entry.isIntersecting : true
        visible = inView && !isHidden
      })
      io.observe(canvas)
    }

    const onVisibilityChange = () => {
      isHidden = document.hidden
      visible = inView && !isHidden
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const loop = (t: number) => {
      rafId = requestAnimationFrame(loop)
      if (!visible || !w || !h) return
      if (!shouldDrawFrame(t, lastTime, FRAME_MS)) return
      lastTime = t
      stepDots(dots, w, h, 2)
      drawFrame()
    }

    rafId = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(rafId)
      ro?.disconnect()
      io?.disconnect()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [loadingStyle, still, count])

  const rootClass = [
    'dsLoader',
    'dsLoader--section',
    `dsLoader--${loadingStyle}`,
    still ? 'dsLoader--still' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClass} style={style}>
      <div className="dsLoader__stage" aria-hidden="true">
        {loadingStyle === 'constellation' ? (
          <canvas ref={canvasRef} className="dsLoader__canvas" />
        ) : (
          <div className="dsLoader__dots">
            {starfieldPoints.map((pt, i) => (
              <span
                key={i}
                className={pt.className}
                style={{
                  left: `${pt.x}%`,
                  top: `${pt.y}%`,
                  animationDelay: `${pt.delay}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
